import { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import Navbar from '../components/Navbar'
import ReportModal from '../components/ReportModal'
import { useAuth, displayUser, isTeenAccount } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import SkeletonLoader from '../components/SkeletonLoader'
import OnlineDot from '../components/OnlineDot'
import { useBlocks } from '../hooks/useBlocks'
import { decodeEntities } from '../lib/decodeEntities'

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1)  return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)  return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7)  return `${days}d ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function Avatar({ grad, url, initial, size = 40 }) {
  return (
    <div
      className="msg-avatar"
      style={{ background: grad ?? 'linear-gradient(135deg,#7C3AED,#EC4899)', width: size, height: size, fontSize: size * 0.4 }}
    >
      {url
        ? <img src={url} alt={initial} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
        : initial}
    </div>
  )
}

// ─── ConversationList ───────────────────────────────────────────────────────────
function ConversationList({ conversations, activeId, onSelect, onDelete, currentUserId }) {
  if (!conversations.length) {
    return (
      <div className="msg-empty-state">
        <div className="msg-empty-state__icon">💬</div>
        <p>No messages yet</p>
        <span>Visit someone's profile and hit "Message" to start a conversation.</span>
      </div>
    )
  }

  return (
    <ul className="msg-convo-list">
      {conversations.map(c => (
        <li key={c.id} style={{ position: 'relative' }}>
          <button
            className={`msg-convo-item${c.id === activeId ? ' msg-convo-item--active' : ''}${c.unread > 0 ? ' msg-convo-item--unread' : ''}`}
            onClick={() => onSelect(c)}
          >
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <Avatar grad={c.partner.avatarGrad} url={c.partner.avatarUrl} initial={c.partner.displayName?.[0]?.toUpperCase() ?? '?'} />
              <OnlineDot userId={c.partner.id} size={10} />
            </div>
            <div className="msg-convo-item__body">
              <div className="msg-convo-item__top">
                <span className="msg-convo-item__name">{decodeEntities(c.partner.displayName ?? c.partner.username)}</span>
                {c.updatedAt && <span className="msg-convo-item__time">{timeAgo(c.updatedAt)}</span>}
              </div>
              <div className="msg-convo-item__preview">
                {c.lastMessage ? (
                  <>{c.lastSenderId === currentUserId ? <strong>You: </strong> : ''}{c.lastMessage}</>
                ) : <em>No messages yet</em>}
              </div>
            </div>
            {c.unread > 0 && <span className="msg-badge">{c.unread > 9 ? '9+' : c.unread}</span>}
          </button>
          <button
            className="msg-convo-item__delete"
            title="Delete conversation"
            onClick={(e) => { e.stopPropagation(); onDelete(c.id) }}
          >🗑</button>
        </li>
      ))}
    </ul>
  )
}

// ─── ChatPanel ─────────────────────────────────────────────────────────────────
function ChatPanel({ conversation, currentUser, onMessageSent, onBack, blockedIds }) {
  const { getToken } = useAuth()
  const [messages, setMessages]   = useState([])
  const [input, setInput]         = useState('')
  const [loading, setLoading]     = useState(true)
  const [sending, setSending]     = useState(false)
  const [error, setError]         = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [editText, setEditText]   = useState('')
  const [editSaving, setEditSaving] = useState(false)
  const [reportingMsgId, setReportingMsgId] = useState(null)
  const [showNewMsg, setShowNewMsg] = useState(false)
  const bottomRef     = useRef(null)
  const inputRef      = useRef(null)
  const messagesRef   = useRef(null)

  function isNearBottom() {
    const el = messagesRef.current
    if (!el) return true
    return el.scrollHeight - el.scrollTop - el.clientHeight < 100
  }

  const fetchMessages = useCallback(async () => {
    if (!conversation.id) { setLoading(false); return }
    setLoading(true)
    try {
      const token = await getToken()
      const res = await axios.get(`/api/messages/${conversation.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setMessages(res.data)
      setError(null)
      // Scroll to bottom after initial load
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'auto' }), 50)
    } catch {
      setError('Failed to load messages')
    } finally {
      setLoading(false)
    }
  }, [conversation.id, getToken])

  useEffect(() => {
    fetchMessages()
    inputRef.current?.focus()
  }, [fetchMessages])

  // ── Realtime subscription for messages ──
  useEffect(() => {
    if (!conversation.id) return

    let channel
    try {
    channel = supabase
      .channel(`chat-${conversation.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversation.id}`,
      }, (payload) => {
        const msg = payload.new
        if (blockedIds.has(msg.sender_id)) return
        setMessages(prev => {
          // Skip if we already have this message (sent by us via API response)
          if (prev.some(m => m.id === msg.id)) return prev
          // Skip our own messages (already added optimistically)
          if (msg.sender_id === currentUser.id) {
            // Replace optimistic message with real data
            const optIdx = prev.findIndex(m => typeof m.id === 'string' && m.id.startsWith('opt-'))
            if (optIdx !== -1) {
              const updated = [...prev]
              updated[optIdx] = {
                id: msg.id,
                senderId: msg.sender_id,
                content: msg.content,
                createdAt: msg.created_at,
                mine: true,
                edited: false,
              }
              return updated
            }
            return prev
          }
          return [...prev, {
            id: msg.id,
            senderId: msg.sender_id,
            content: msg.content,
            createdAt: msg.created_at,
            mine: false,
            edited: false,
          }]
        })

        // For incoming messages from the other user
        if (msg.sender_id !== currentUser.id) {
          if (isNearBottom()) {
            setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50)
          } else {
            setShowNewMsg(true)
          }
          // Mark as read since conversation is open
          markRead(conversation.id)
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversation.id}`,
      }, (payload) => {
        const msg = payload.new
        setMessages(prev => prev.map(m => m.id === msg.id ? {
          ...m,
          content: msg.content,
          edited: !!(msg.updated_at && Math.abs(new Date(msg.updated_at) - new Date(msg.created_at)) > 1000),
        } : m))
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'messages',
      }, (payload) => {
        setMessages(prev => prev.filter(m => m.id !== payload.old.id))
      })
      .subscribe()
    } catch (err) {
      console.warn('[MessagesPage] Chat realtime subscription failed:', err)
    }

    return () => { if (channel) supabase.removeChannel(channel) }
  }, [conversation.id, currentUser.id]) // eslint-disable-line react-hooks/exhaustive-deps

  async function markRead(convoId) {
    try {
      const token = await getToken()
      await axios.patch(`/api/messages/${convoId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      })
      window.dispatchEvent(new Event('verse:messages-read'))
    } catch (err) {
      console.error('[MessagesPage] Failed to mark conversation read:', err)
    }
  }

  async function deleteMessage(msgId) {
    setMessages(prev => prev.filter(m => m.id !== msgId))
    try {
      const token = await getToken()
      await axios.delete(`/api/messages/${msgId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
    } catch {
      fetchMessages()
    }
  }

  function startEditing(msg) {
    setEditingId(msg.id)
    setEditText(msg.content)
  }

  function cancelEditing() {
    setEditingId(null)
    setEditText('')
  }

  async function saveEdit() {
    if (!editText.trim() || editSaving) return
    setEditSaving(true)
    try {
      const token = await getToken()
      const { data: updated } = await axios.put(`/api/messages/${editingId}`, { content: editText }, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setMessages(prev => prev.map(m => m.id === editingId ? { ...m, content: updated.content, edited: true } : m))
      setEditingId(null)
      setEditText('')
    } catch (err) {
      console.error('[MessagesPage] edit message failed:', err)
      setError('Failed to edit message')
    } finally {
      setEditSaving(false)
    }
  }

  function handleEditKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      saveEdit()
    }
    if (e.key === 'Escape') {
      cancelEditing()
    }
  }

  async function sendMessage(e) {
    e.preventDefault()
    if (!input.trim() || sending) return

    const content = input.trim()
    setInput('')
    setSending(true)

    // Optimistic update
    const optimistic = {
      id: `opt-${Date.now()}`,
      senderId: currentUser.id,
      content,
      createdAt: new Date().toISOString(),
      mine: true,
    }
    setMessages(prev => [...prev, optimistic])
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'auto' }), 50)

    try {
      const token = await getToken()
      const payload = conversation.id
        ? { conversationId: conversation.id, content }
        : { recipientId: conversation.partner.id, content }

      const { data: sent } = await axios.post('/api/messages', payload, {
        headers: { Authorization: `Bearer ${token}` },
      })

      // Replace optimistic message with real server data
      setMessages(prev => prev.map(m => m.id === optimistic.id ? {
        id: sent.id,
        senderId: sent.senderId,
        content: sent.content,
        createdAt: sent.createdAt,
        mine: true,
        edited: false,
      } : m))

      // If this was a new conversation, notify parent with the real convo ID
      if (!conversation.id && sent.conversationId) {
        onMessageSent(sent.conversationId, content)
        return
      }

      onMessageSent(conversation.id, content)
    } catch {
      // Remove optimistic message on failure
      setMessages(prev => prev.filter(m => m.id !== optimistic.id))
      setInput(content)
      setError('Failed to send message')
    } finally {
      setSending(false)
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(e)
    }
  }

  function scrollToBottom() {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    setShowNewMsg(false)
  }

  return (
    <div className="msg-chat" style={{ position: 'relative' }}>
      {/* Header */}
      <div className="msg-chat__header">
        <Link to={`/user/${conversation.partner.username}`} style={{ textDecoration: 'none', flexShrink: 0 }}>
          <Avatar
            grad={conversation.partner.avatarGrad}
            url={conversation.partner.avatarUrl}
            initial={conversation.partner.displayName?.[0]?.toUpperCase() ?? '?'}
            size={36}
          />
        </Link>
        {onBack && <button className="msg-chat__back-btn" onClick={onBack}>← Back</button>}
        <div className="msg-chat__header-info">
          <Link to={`/user/${conversation.partner.username}`} className="msg-chat__header-name" style={{ textDecoration: 'none', color: 'inherit' }}>
            {conversation.partner.displayName ?? conversation.partner.username}
          </Link>
          <Link to={`/user/${conversation.partner.username}`} className="msg-chat__header-username">
            @{conversation.partner.username}
          </Link>
        </div>
      </div>

      {/* Messages */}
      <div
        className="msg-chat__messages"
        ref={messagesRef}
        onScroll={() => { if (isNearBottom()) setShowNewMsg(false) }}
      >
        {loading ? (
          <div className="msg-chat__loading">Loading…</div>
        ) : error ? (
          <div className="msg-chat__error">{error}</div>
        ) : messages.length === 0 ? (
          <div className="msg-chat__empty">
            <div className="msg-empty-state__icon">👋</div>
            <p>Say hello to {conversation.partner.displayName ?? conversation.partner.username}!</p>
          </div>
        ) : (
          messages.map((m, i) => {
            const prevMine = i > 0 && messages[i - 1].mine === m.mine
            return (
              <div
                key={m.id}
                className={`msg-bubble-wrap${m.mine ? ' msg-bubble-wrap--mine' : ''}${prevMine ? ' msg-bubble-wrap--consecutive' : ''}`}
              >
                {m.mine ? (
                  editingId === m.id ? (
                    <div className="msg-edit-inline">
                      <textarea
                        className="msg-edit-inline__input"
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                        onKeyDown={handleEditKeyDown}
                        maxLength={5000}
                        rows={2}
                        autoFocus
                      />
                      <div className="msg-edit-inline__actions">
                        <button className="btn btn-primary" style={{ fontSize: '0.78rem', padding: '4px 12px' }} onClick={saveEdit} disabled={!editText.trim() || editSaving}>
                          {editSaving ? 'Saving…' : 'Save'}
                        </button>
                        <button className="btn" style={{ fontSize: '0.78rem', padding: '4px 12px' }} onClick={cancelEditing} disabled={editSaving}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="msg-bubble-row">
                      <button
                        className="msg-action-btn msg-edit-btn"
                        onClick={() => startEditing(m)}
                        aria-label="Edit message"
                        title="Edit"
                      >
                        <PencilIcon />
                      </button>
                      <button
                        className="msg-action-btn msg-delete-btn"
                        onClick={() => deleteMessage(m.id)}
                        aria-label="Delete message"
                        title="Delete"
                      >
                        <TrashIcon />
                      </button>
                      <div className="msg-bubble msg-bubble--mine">
                        {decodeEntities(m.content)}
                        {m.edited && <span className="msg-edited-tag">(edited)</span>}
                      </div>
                    </div>
                  )
                ) : (
                  <div className="msg-theirs-row">
                    <div className="msg-bubble msg-bubble--theirs">
                      {decodeEntities(m.content)}
                      {m.edited && <span className="msg-edited-tag">(edited)</span>}
                    </div>
                    <button
                      className="msg-report-flag"
                      onClick={() => setReportingMsgId(m.id)}
                      title="Report message"
                    >
                      &#9873;
                    </button>
                  </div>
                )}
                <span className="msg-bubble-time">{timeAgo(m.createdAt)}</span>
              </div>
            )
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* New message indicator */}
      {showNewMsg && (
        <button
          onClick={scrollToBottom}
          style={{
            position: 'absolute',
            bottom: 70,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '6px 16px',
            borderRadius: 20,
            background: 'var(--purple)',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.8rem',
            fontWeight: 600,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            zIndex: 10,
          }}
        >
          ↓ New message
        </button>
      )}

      {/* Input */}
      <form className="msg-chat__input-row" onSubmit={sendMessage}>
        <textarea
          ref={inputRef}
          className="msg-chat__textarea"
          placeholder={`Message ${conversation.partner.displayName ?? conversation.partner.username}…`}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={5000}
          rows={1}
        />
        <button
          type="submit"
          className="msg-chat__send-btn btn btn-primary"
          disabled={!input.trim() || sending}
          aria-label="Send"
        >
          <SendIcon />
        </button>
      </form>
      {reportingMsgId && (
        <ReportModal contentType="message" contentId={reportingMsgId} onClose={() => setReportingMsgId(null)} />
      )}
    </div>
  )
}

// ─── Page ───────────────────────────────────────────────────────────────────────
export default function MessagesPage() {
  const { user, profile, getToken } = useAuth()
  const currentUser = displayUser(user, profile)
  const navigate     = useNavigate()
  const [searchParams] = useSearchParams()

  const [conversations, setConversations] = useState([])
  const [active, setActive]               = useState(null)
  const [loading, setLoading]             = useState(true)
  const activeRef = useRef(null)
  const blockedIds = useBlocks()

  // Keep activeRef in sync
  useEffect(() => { activeRef.current = active }, [active])

  // Load conversations
  useEffect(() => {
    async function load() {
      setLoading(true)
      try {
        const token = await getToken()
        const res   = await axios.get('/api/messages', { headers: { Authorization: `Bearer ${token}` } })
        setConversations(res.data)

        // If ?with=username was passed, open or initiate that conversation
        const withUser = searchParams.get('with')
        if (withUser) {
          const existing = res.data.find(c => c.partner.username === withUser)
          if (existing) {
            setActive(existing)
          } else {
            // Resolve recipient ID and create a placeholder conversation
            try {
              const { data: resolved } = await axios.get(`/api/messages/with/${withUser}`, { headers: { Authorization: `Bearer ${token}` } })
              setActive({
                id:          resolved.conversationId ?? null,
                partner:     { id: resolved.recipientId, username: withUser, displayName: resolved.displayName ?? withUser, avatarGrad: resolved.avatarGrad ?? null, avatarUrl: resolved.avatarUrl ?? null },
                lastMessage: null,
                unread:      0,
              })
            } catch (err) {
              console.error('[MessagesPage] resolve conversation partner failed:', err)
            }
          }
        }
      } catch (err) {
        console.error('[MessagesPage] load conversations failed:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [getToken, searchParams])

  // ── Realtime subscription for conversation list updates ──
  useEffect(() => {
    if (!user) return

    let channel
    try {
    channel = supabase
      .channel('conversation-list')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'conversations',
      }, (payload) => {
        const updated = payload.new
        const partnerId = updated.user1_id === user.id ? updated.user2_id : updated.user1_id
        if (blockedIds.has(partnerId)) return
        setConversations(prev => {
          const idx = prev.findIndex(c => c.id === updated.id)
          if (idx === -1) return prev

          const existing = prev[idx]
          const isUser1 = updated.user1_id === user.id
          const unread = isUser1 ? (updated.unread_count_user1 ?? 0) : (updated.unread_count_user2 ?? 0)
          const isActive = activeRef.current?.id === updated.id

          const updatedConvo = {
            ...existing,
            lastMessage: updated.last_message,
            lastSenderId: updated.last_sender_id,
            unread: isActive ? 0 : unread,
            updatedAt: updated.updated_at,
          }

          // Move updated conversation to top
          return [updatedConvo, ...prev.filter((_, i) => i !== idx)]
        })
      })
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'conversations',
      }, async () => {
        // New conversation — refetch to get partner profile info
        try {
          const token = await getToken()
          const res = await axios.get('/api/messages', { headers: { Authorization: `Bearer ${token}` } })
          setConversations(res.data)
        } catch (err) {
          console.error('[MessagesPage] Failed to refetch conversations after realtime INSERT:', err)
        }
      })
      .subscribe()
    } catch (err) {
      console.warn('[MessagesPage] Conversation list realtime subscription failed:', err)
    }

    return () => { if (channel) supabase.removeChannel(channel) }
  }, [user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleSelect(convo) {
    setActive(convo)
    // Mark local unread as 0
    setConversations(prev => prev.map(c => c.id === convo.id ? { ...c, unread: 0 } : c))
    // Tell useUnreadMessages to refetch the global badge count
    window.dispatchEvent(new Event('verse:messages-read'))
  }

  function handleMessageSent(convoId, content) {
    setConversations(prev => {
      const exists = prev.find(c => c.id === convoId)
      if (exists) {
        return [
          { ...exists, lastMessage: content, updatedAt: new Date().toISOString() },
          ...prev.filter(c => c.id !== convoId),
        ]
      }
      // New conversation — add to top using active's partner info
      if (active && !active.id) {
        return [
          { ...active, id: convoId, lastMessage: content, updatedAt: new Date().toISOString() },
          ...prev,
        ]
      }
      return prev
    })
  }

  async function handleDeleteConversation(convoId) {
    if (!window.confirm('Delete this conversation? It will be removed from your inbox.')) return
    try {
      const token = await getToken()
      await axios.delete(`/api/messages/conversations/${convoId}`, { headers: { Authorization: `Bearer ${token}` } })
      setConversations(prev => prev.filter(c => c.id !== convoId))
      if (active?.id === convoId) setActive(null)
    } catch (err) {
      console.error('[messages] delete conversation failed:', err)
    }
  }

  const totalUnread = conversations.reduce((s, c) => s + (c.unread ?? 0), 0)

  if (isTeenAccount(profile)) {
    return (
      <div className="messages-page">
        <Navbar />
        <div style={{ textAlign: 'center', padding: '80px 24px', color: 'var(--text-muted)' }}>
          Messaging is not available for users under 18.
        </div>
      </div>
    )
  }

  return (
    <div className="messages-page">
      <Navbar />

      <div className={`msg-layout${active ? ' msg-layout--chat-open' : ''}`}>
        {/* Sidebar */}
        <aside className="msg-sidebar">
          <div className="msg-sidebar__header">
            <h2 className="msg-sidebar__title">
              Messages
              {totalUnread > 0 && <span className="msg-badge">{totalUnread > 99 ? '99+' : totalUnread}</span>}
            </h2>
          </div>

          {loading ? (
            <div className="msg-sidebar__loading"><SkeletonLoader type="list-item" count={5} /></div>
          ) : (
            <ConversationList
              conversations={conversations}
              activeId={active?.id}
              onSelect={handleSelect}
              onDelete={handleDeleteConversation}
              currentUserId={user?.id}
            />
          )}
        </aside>

        {/* Main panel */}
        <main className="msg-main">
          {active ? (
            <ChatPanel
              key={active.id ?? active.partner.username}
              conversation={active}
              currentUser={{ id: user?.id }}
              blockedIds={blockedIds}
              onBack={() => setActive(null)}
              onMessageSent={(id, content) => {
                // If this was a new conversation, update the ID
                handleMessageSent(id, content)
                if (!active.id) {
                  setActive(prev => ({ ...prev, id }))
                }
              }}
            />
          ) : (
            <div className="msg-no-selection">
              <div className="msg-empty-state__icon">✉️</div>
              <p>Select a conversation</p>
              <span>Or visit a user's profile and click "Message" to start one.</span>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4h6v2" />
    </svg>
  )
}

function PencilIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  )
}

function SendIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  )
}
